import { notifyInfo, sendError } from "./bugsnag";

export const BUGSNAG_APP = {
  GROWTH_PORTAL: "growth-portal",
  DASHBOARD: "dashboard",
} as const;

export const ONBOARDING_FLOWS = {
  CREATE_APPLICATION: "onboarding_create_application",
  ADVERTISER_INTELLIGENCE: "onboarding_advertiser_intelligence",
  DRAFT_RESTORE: "onboarding_draft_restore",
  DRAFT_RESTORE_HYDRATE: "onboarding_draft_restore_hydrate",
  DEEPLINK_HYDRATE: "onboarding_deeplink_hydrate",
  AUDIENCE_CONFIRM: "onboarding_audience_confirm",
  PRICING_CONFIRM: "onboarding_pricing_confirm",
  SAVE_DRAFT: "onboarding_save_draft",
  SUBMIT_PAYMENT: "onboarding_submit_payment",
  CHAIN_PAYMENT: "onboarding_chain_payment",
  TX_HASH_PROCESS: "onboarding_tx_hash_process",
  PAYMENT_POLL: "onboarding_payment_poll",
  PAYMENT_CONFIG: "onboarding_payment_config",
  GET_CONFIGS: "onboarding_get_configs",
  WALLET: "onboarding_wallet",
  ENTERPRISE_CONTACT: "onboarding_enterprise_contact",
  APPLICATION_UPDATE: "onboarding_application_update",
  /** Landing Contact Sales 表单（非 Onboarding Enterprise） */
  LANDING_CONTACT_SALES: "landing_contact_sales",
  /** Landing 导航等处的 passport 拉取 */
  USER_PROFILE: "growth_portal_user_profile",
} as const;

export const DASHBOARD_FLOWS = {
  CAMPAIGN_LIST: "dashboard_campaign_list",
  CAMPAIGN_DETAIL: "dashboard_campaign_detail",
  APPLICATION_DETAIL: "dashboard_application_detail",
  LOGOUT: "dashboard_logout",
  USER_PROFILE: "dashboard_user_profile",
} as const;

export type BugsnagAppId = (typeof BUGSNAG_APP)[keyof typeof BUGSNAG_APP];

const USER_REJECTED_PATTERNS = [
  "User rejected",
  "user rejected",
  "ACTION_REJECTED",
];

export type BugsnagReportContext = Record<string, unknown>;

export type ApplicationMutationBugsnagMeta = {
  flow?: string;
  context?: BugsnagReportContext;
};

export function isUserRejectedWalletError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error !== null && "message" in error
        ? String((error as { message?: string }).message)
        : String(error);
  return USER_REJECTED_PATTERNS.some((pattern) => message.includes(pattern));
}

export function reportGrowthPortalError(
  flow: string,
  error: unknown,
  context?: BugsnagReportContext,
): void {
  if (isUserRejectedWalletError(error)) {
    return;
  }
  sendError(error, {
    app: BUGSNAG_APP.GROWTH_PORTAL,
    flow,
    ...context,
  });
}

/** 可接受业务路径（如慢确认），以 info 上报，避免与真实 Error 混淆 */
export function reportGrowthPortalInfo(
  flow: string,
  message: string,
  context?: BugsnagReportContext,
): void {
  notifyInfo(message, {
    app: BUGSNAG_APP.GROWTH_PORTAL,
    flow,
    ...context,
  });
}

export function reportDashboardError(
  flow: string,
  error: unknown,
  context?: BugsnagReportContext,
): void {
  sendError(error, {
    app: BUGSNAG_APP.DASHBOARD,
    flow,
    ...context,
  });
}

export function resolveApplicationMutationFlow(
  vars: {
    kind: "create" | "update";
    data?: { intent?: string };
  },
  overrideFlow?: string,
): string {
  if (overrideFlow) {
    return overrideFlow;
  }
  if (vars.kind === "create") {
    return ONBOARDING_FLOWS.CREATE_APPLICATION;
  }
  const intent = vars.data?.intent;
  switch (intent) {
    case "SUBMIT_PAYMENT":
      return ONBOARDING_FLOWS.SUBMIT_PAYMENT;
    case "DRAFT":
      return ONBOARDING_FLOWS.SAVE_DRAFT;
    case "SUBMIT_SALES":
      return ONBOARDING_FLOWS.ENTERPRISE_CONTACT;
    default:
      return ONBOARDING_FLOWS.APPLICATION_UPDATE;
  }
}

export async function withDashboardBugsnag<T>(
  flow: string,
  context: BugsnagReportContext,
  fn: () => Promise<T>,
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    reportDashboardError(flow, error, context);
    throw error;
  }
}
